import { useCoreEvents } from '@carvana/core-events';
import { useCallback, useMemo, useState } from 'react';
import {
  getMfeDefinition,
  isFromMfeSource,
  type MfeKey
} from '~/constants/common/MfeModules';
import { selectIsMfeReady, useMfeLoadStore } from '~/state/common/MfeLoadState';

const REPLAY_OPTIONS = { withHistory: true };

type Options = {
  // For users who never interact with the web MFE (e.g. native webviews), so callers never wait.
  isBypassed?: boolean;
};

export default function useMfeReady(
  key: MfeKey,
  { isBypassed = false }: Options = {}
) {
  const { moduleName, readyEvent, readySource, replayReady, unreadyEvent } =
    getMfeDefinition(key);
  const selectIsReady = useMemo(
    () => selectIsMfeReady(moduleName),
    [moduleName]
  );
  const isReadyFromStore = useMfeLoadStore(selectIsReady);

  // Fallback for pages that don't mount MfeEventsListener. A replay is only safe without an
  // unready event, since it could otherwise resurrect a ready that was later withdrawn.
  const [isReadyFromListener, setReadyFromListener] = useState(false);
  const canReplay = replayReady && !unreadyEvent;

  const handleReady = useCallback(
    (payload: unknown) => {
      if (isFromMfeSource(payload, readySource)) setReadyFromListener(true);
    },
    [readySource]
  );
  const handleUnready = useCallback(
    (payload: unknown) => {
      if (isFromMfeSource(payload, readySource)) setReadyFromListener(false);
    },
    [readySource]
  );

  useCoreEvents(
    readyEvent,
    handleReady,
    canReplay ? REPLAY_OPTIONS : undefined
  );
  useCoreEvents(unreadyEvent, handleUnready);

  return isBypassed || isReadyFromStore || isReadyFromListener;
}
